import { useCallback, useState, type ReactNode } from 'react';
import {
  useAudio,
  useConfig,
  useDispatch,
  useMachineState,
  useRobotStatus,
  useSyncStatus,
  useUnitIdentity,
} from '../app/context';
import logoOnDark from '../assets/evesix-logo-white.png';
import logoOnLight from '../assets/evesix-logo-black.png';
import { SCREEN_STEP, WORKFLOW_STEPS } from '../domain/catalog';
import { canSignOut } from '../domain/machine';
import { useClock } from '../hooks/useClock';
import { useStageScale } from '../hooks/useStageScale';
import { useTheme } from '../hooks/useTheme';
import { cx } from './cx';
import { HospitalIcon, MoonIcon, SunIcon, UserIcon } from './icons';
import { LampControlPanel, LampPill, ManualLampHazard } from './LampControl';
import styles from './Stage.module.css';

export const STAGE_WIDTH = 1280;
export const STAGE_HEIGHT = 800;

export function Stage({ children }: { readonly children: ReactNode }) {
  const scale = useStageScale(STAGE_WIDTH, STAGE_HEIGHT);
  const [lampPanelOpen, setLampPanelOpen] = useState(false);
  const closeLampPanel = useCallback(() => setLampPanelOpen(false), []);

  return (
    <div className={styles.viewport}>
      <div
        className={styles.stage}
        style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT, transform: `scale(${scale})` }}
      >
        <TopBar onOpenLampControl={() => setLampPanelOpen(true)} />
        <BottomBar />
        <main className={styles.content}>{children}</main>
        {/* Stage-level, not inside the top bar: its entrance animation would trap fixed overlays. */}
        <ManualLampHazard />
        {lampPanelOpen && <LampControlPanel onClose={closeLampPanel} />}
      </div>
    </div>
  );
}

function TopBar({ onOpenLampControl }: { readonly onOpenLampControl: () => void }) {
  const { unitId, hospitalName } = useUnitIdentity();
  const audio = useAudio();
  const clock = useClock();
  const { connected } = useRobotStatus();
  const [theme, toggleTheme] = useTheme();
  const dark = theme === 'dark';

  const onToggle = () => {
    audio.play(dark ? 'toggleOff' : 'toggleOn');
    toggleTheme();
  };

  return (
    <header className={styles.topBar}>
      <div className={cx(styles.group, styles.brand)}>
        <img className={styles.logo} src={dark ? logoOnDark : logoOnLight} alt="Evesix" />
        <div className={styles.unitLabel}>UVC Disinfection Unit</div>
        <div className={styles.unitChip}>{unitId}</div>
        <span className={styles.divider} />
        <div className={styles.hospital}>
          <HospitalIcon size={16} className={styles.hospitalIcon} />
          <span>{hospitalName}</span>
        </div>
      </div>
      <div className={cx(styles.group, styles.statusGroup)}>
        <LampPill onOpen={onOpenLampControl} />
        <span className={styles.divider} />
        <div className={cx(styles.link, !connected && styles.offline)}>
          <span className={styles.linkDot} />
          <span>{connected ? 'Connected' : 'Offline'}</span>
        </div>
        <SyncBadge />
        <span className={styles.divider} />
        <time className={styles.clock}>{clock}</time>
        <span className={styles.divider} />
        <button
          type="button"
          className={styles.themeToggle}
          onClick={onToggle}
          aria-label={`Switch to ${dark ? 'light' : 'dark'} theme`}
        >
          {dark ? <SunIcon size={14} /> : <MoonIcon size={14} />}
          {dark ? 'LIGHT' : 'DARK'}
        </button>
      </div>
    </header>
  );
}

/**
 * Who is signed in, and LOG OUT so the next operator can sign in. Offered only where no lamp is
 * running: during a countdown or a cycle the operator stops it first.
 */
function OperatorBadge() {
  const state = useMachineState();
  const dispatch = useDispatch();
  if (!state.operator) return null;
  return (
    <div className={styles.operator}>
      <UserIcon size={14} className={styles.hospitalIcon} />
      <span>{state.operator.name.toUpperCase()}</span>
      {canSignOut(state) && (
        <button
          type="button"
          className={styles.signOut}
          onClick={() => dispatch({ type: 'SIGN_OUT' })}
        >
          LOG OUT
        </button>
      )}
    </div>
  );
}

/**
 * Offline mode: whether the tablet has data, and what is waiting on it for the server. Records
 * stay on the tablet until uploaded, so "waiting" is never lost, only not yet on the server.
 */
function SyncBadge() {
  const sync = useSyncStatus();
  if (!sync) return null;
  let text: string;
  if (!sync.enrolled) text = 'Not set up';
  else if (sync.online) text = sync.pending ? `Syncing ${sync.pending}` : 'Synced';
  else text = sync.pending ? `No data · ${sync.pending} waiting` : 'No data';
  const warn = !sync.enrolled || sync.rejected > 0;
  return (
    <>
      <span className={styles.divider} />
      <div
        className={cx(styles.link, (!sync.online || warn) && styles.offline)}
        aria-label="Sync with the server"
      >
        <span className={styles.linkDot} />
        <span>{text}</span>
        {sync.rejected > 0 && <span>· {sync.rejected} refused</span>}
      </div>
    </>
  );
}

function BottomBar() {
  const { screen } = useMachineState();
  const { timeAccelerated } = useConfig();
  const current = SCREEN_STEP[screen];

  return (
    <footer className={styles.bottomBar}>
      <ol className={styles.steps} aria-label="Workflow">
        {WORKFLOW_STEPS.map((label, i) => (
          <li
            key={label}
            className={cx(
              styles.step,
              i < current && styles.stepDone,
              i === current && styles.stepCurrent,
            )}
            aria-current={i === current ? 'step' : undefined}
          >
            <span>{String(i + 1).padStart(2, '0')}</span>
            <span>{label}</span>
          </li>
        ))}
      </ol>
      <div className={cx(styles.group, styles.footerMeta)}>
        <OperatorBadge />
        {timeAccelerated && <span className={styles.demoTag}>DEMO · TIME COMPRESSED ×4 / ×20</span>}
        <span>UV-C 254&nbsp;NM · FYBATEX MEDTECH</span>
      </div>
    </footer>
  );
}
